import { useCallback, useRef, useEffect, useMemo } from 'react';

import { PaymentsResource } from '../../core/resources/payments';

import { getGlobalApiClient } from './useApiQuery';

// Define the payment type to match API response
export interface Payment {
    id: string;
    status: string;
    subStatus: string;
    requireAction: 'none' | 'redirect' | 'redirect_to_payment' | 'error' | 'radar' | 'stripe_express_checkout';
    requireActionData?: {
        type: 'redirect' | 'redirect_to_payment' | 'threeds_auth' | 'processor_auth' | 'error' | 'stripe_radar' | 'finix_radar' | 'radar' | 'kesspay_auth' | 'trustflow_auth' | 'mastercard_auth' | 'ngenius_3ds' | 'stripe_express_checkout';
        url?: string;
        processed: boolean;
        processorId?: string;
        metadata?: {
            type: 'redirect' | 'redirect_to_payment' | 'stripe_radar' | 'finix_radar';
            redirect?: {
                redirectUrl: string;
                returnUrl: string;
            };
            threedsSession?: {
                externalSessionId: string;
                acsChallengeUrl: string;
                acsTransID: string;
                messageVersion: string;
            };
            radar?: {
                merchantId?: string;
                environment?: 'sandbox' | 'live';
                orderId?: string;
                publishableKey?: string;
            };
            provider?: string;
            isTest?: boolean;
        };
        redirectUrl?: string;
        resumeToken?: string;
        message?: string;
        errorCode?: string;
    };
    order?: {
        id: string;
        checkoutSessionId: string;
    };
}

export interface PollingOptions {
    onRequireAction?: (payment: Payment, stop: () => void) => void;
    onSuccess?: (payment: Payment) => void;
    onFailure?: (error: string) => void;
    maxAttempts?: number;
    pollInterval?: number;
}

export interface PaymentPollingHook {
    startPolling: (
        paymentId: string,
        options?: PollingOptions,
    ) => { stop: () => void; isPolling: () => boolean };
    stopPolling: () => void;
    isPolling: () => boolean;
}

export function usePaymentPolling(): PaymentPollingHook {
    const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const attemptsRef = useRef<number>(0);
    const isPollingRef = useRef<boolean>(false);
    const isMountedRef = useRef<boolean>(true);
    const currentPaymentIdRef = useRef<string | null>(null);

    // Create payments resource client
    const paymentsResource = useMemo(() => {
        try {
            return new PaymentsResource(getGlobalApiClient());
        } catch (error) {
            throw new Error('Failed to initialize payments resource: ' + (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);

    // Track mounted state and cleanup
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            stopPolling();
        };
    }, []);

    const stopPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        isPollingRef.current = false;
        currentPaymentIdRef.current = null;

        if (isMountedRef.current) {
            // Component is still mounted
        }
    }, []);

    const startPolling = useCallback(
        (paymentId: string, options: PollingOptions = {}) => {
            if (!paymentId) {
                return { stop: stopPolling, isPolling: () => false };
            }

            // Prevent multiple polling sessions for the same payment
            if (currentPaymentIdRef.current === paymentId && isPollingRef.current) {
                return { stop: stopPolling, isPolling: () => isPollingRef.current };
            }

            // Clean up any existing polling
            stopPolling();

            // Don't start polling if component is unmounted
            if (!isMountedRef.current) {
                return { stop: stopPolling, isPolling: () => false };
            }

            // Reset attempts counter and set current payment
            attemptsRef.current = 0;
            isPollingRef.current = true;
            currentPaymentIdRef.current = paymentId;

            const { onRequireAction, onSuccess, onFailure, maxAttempts = 20, pollInterval = 1500 } = options;

            console.log('🔄 [usePaymentPolling] Starting polling...', {
                paymentId,
                maxAttempts,
                pollInterval: `${pollInterval}ms`,
            });

            const checkPaymentStatus = async () => {
                // Stop if component was unmounted or polling was stopped
                if (!isMountedRef.current || !isPollingRef.current) {
                    return;
                }

                try {
                    attemptsRef.current++;
                    console.log(`🔄 [usePaymentPolling] Polling attempt ${attemptsRef.current}/${maxAttempts} for payment ${paymentId}...`);

                    const payment = await paymentsResource.getPaymentStatus(paymentId);
                    console.log(`📊 [usePaymentPolling] Payment status:`, {
                        id: payment.id,
                        status: payment.status,
                        subStatus: payment.subStatus,
                        requireAction: payment.requireAction,
                    });

                    // Check again after async operation
                    if (!isMountedRef.current || !isPollingRef.current) {
                        return;
                    }

                    // Type guard and validation
                    if (!payment?.id) {
                        return;
                    }

                    // 🔒 CRITICAL: Check for successful payment FIRST
                    // Even if requireAction is set, if payment succeeded, we should call onSuccess
                    // This handles the case where payment succeeds after redirect (3DS, etc.)
                    if (
                        payment.status === 'succeeded' ||
                        (payment.status === 'pending' && payment.subStatus === 'authorized')
                    ) {
                        console.log('✅ [usePaymentPolling] Payment succeeded! Stopping polling.');
                        stopPolling();
                        if (isMountedRef.current && onSuccess) {
                            onSuccess(payment);
                        }
                        return;
                    }

                    // Check if payment requires action (and hasn't been processed yet)
                    // Only if payment is NOT yet succeeded
                    if (payment.requireAction !== 'none' && payment.requireActionData && !payment.requireActionData.processed) {
                        console.log('⚠️ [usePaymentPolling] Payment requires NEW action (not yet processed) - stopping polling');
                        stopPolling();
                        if (isMountedRef.current && onRequireAction) {
                            onRequireAction(payment, stopPolling);
                        }
                        return;
                    }

                    // Check for failed payment (non-succeeded and not pending)
                    if (payment.status !== 'succeeded' && payment.status !== 'pending') {
                        console.error('❌ [usePaymentPolling] Payment failed - stopping polling');
                        stopPolling();
                        if (isMountedRef.current && onFailure) {
                            onFailure(payment.status || 'Payment failed');
                        }
                        return;
                    }

                    // Stop after max attempts
                    if (attemptsRef.current >= maxAttempts) {
                        console.warn('⏱️ [usePaymentPolling] Max attempts reached - stopping polling');
                        stopPolling();
                        if (isMountedRef.current && onFailure) {
                            onFailure('Payment verification timeout');
                        }
                    } else {
                        console.log(`⏳ [usePaymentPolling] Payment still pending - will retry in ${pollInterval}ms...`);
                    }
                } catch (_error) {

                    // Stop polling on repeated errors to prevent infinite loops
                    if (attemptsRef.current >= 3) {
                        stopPolling();
                        if (isMountedRef.current && onFailure) {
                            onFailure('Payment verification failed due to network errors');
                        }
                    }
                    // Continue polling for occasional errors
                }
            };

            // Start polling immediately, but check if still mounted
            if (isMountedRef.current && isPollingRef.current) {
                void checkPaymentStatus();
                pollIntervalRef.current = setInterval(() => {
                    if (isMountedRef.current && isPollingRef.current) {
                        void checkPaymentStatus();
                    } else {
                        stopPolling();
                    }
                }, pollInterval);
            }

            // Return control object
            return {
                stop: stopPolling,
                isPolling: () => isPollingRef.current && isMountedRef.current,
            };
        },
        [paymentsResource, stopPolling],
    );

    return {
        startPolling,
        stopPolling,
        isPolling: () => isPollingRef.current && isMountedRef.current,
    };
}
